import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { BAN_REASON_MAX, BanForm, buildBanBody, validateBanForm } from './helpers';

const EMPTY: BanForm = { userId: '', walletAddress: '', reason: '' };

/**
 * "Ban user" dialog. Without `target` the admin enters exactly one of userId / wallet address;
 * with `target` (e.g. a leaderboard row) the identifier is fixed and only the reason is asked.
 * `onConfirm` performs the API call; the dialog stays open (and keeps its input) if it throws.
 */
export function BanUserDialog({
  open,
  target,
  onClose,
  onConfirm,
}: {
  open: boolean;
  target?: { userId?: string | null; walletAddress?: string | null };
  onClose: () => void;
  onConfirm: (body: {
    userId?: string;
    walletAddress?: string;
    banned: true;
    reason: string;
  }) => Promise<void>;
}) {
  const t = useTranslate();
  const notify = useNotify();
  const [form, setForm] = useState<BanForm>(EMPTY);
  const [busy, setBusy] = useState(false);
  const fixed = !!target;

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const set = (patch: Partial<BanForm>) => setForm((p) => ({ ...p, ...patch }));

  const submit = async () => {
    const err = validateBanForm(form, fixed);
    if (err) {
      notify(t(err, { max: BAN_REASON_MAX }), { type: 'warning' });
      return;
    }
    const body = fixed
      ? {
          ...(target?.userId
            ? { userId: target.userId }
            : { walletAddress: target?.walletAddress || undefined }),
          banned: true as const,
          reason: form.reason.trim(),
        }
      : buildBanBody(form);
    setBusy(true);
    try {
      await onConfirm(body);
    } catch {
      // Caller already notified; keep the dialog open so the input is not lost.
    } finally {
      setBusy(false);
    }
  };

  const targetLabel = target?.userId || target?.walletAddress || '';

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('module.blindBox.banDialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="warning" variant="outlined">
            {t('module.blindBox.banDialogWarning')}
          </Alert>
          {fixed ? (
            <Typography variant="body2">
              {t('module.blindBox.banTargetLabel')}:{' '}
              <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                {targetLabel}
              </Typography>
            </Typography>
          ) : (
            <>
              <TextField
                label={t('module.blindBox.banColUserId')}
                value={form.userId}
                onChange={(e) => set({ userId: e.target.value })}
                size="small"
                fullWidth
                disabled={!!form.walletAddress.trim()}
              />
              <TextField
                label={t('module.blindBox.banWalletAddress')}
                value={form.walletAddress}
                onChange={(e) => set({ walletAddress: e.target.value })}
                size="small"
                fullWidth
                placeholder="0x + 40 hex"
                disabled={!!form.userId.trim()}
                helperText={t('module.blindBox.banIdentifierHint')}
              />
            </>
          )}
          <TextField
            label={t('module.blindBox.banReason')}
            value={form.reason}
            onChange={(e) => set({ reason: e.target.value })}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            inputProps={{ maxLength: BAN_REASON_MAX }}
            helperText={`${form.reason.length}/${BAN_REASON_MAX}`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" color="error" onClick={submit} disabled={busy}>
          {t('module.blindBox.banConfirmButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Confirmation dialog for unbanning a user. `onConfirm` performs the API call. */
export function UnbanConfirmDialog({
  open,
  userLabel,
  onClose,
  onConfirm,
}: {
  open: boolean;
  userLabel: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}) {
  const t = useTranslate();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } catch {
      // Caller already notified.
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('module.blindBox.unbanDialogTitle')}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {t('module.blindBox.lbUnbanConfirm')}
        </Typography>
        {userLabel ? (
          <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>
            {userLabel}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {t('module.blindBox.lbUnban')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
